import { useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamId } from "@/hooks/useTeamId";
import { supabase } from "@/integrations/supabase/client";

export interface Insight {
  key: string;
  priority: "high" | "medium" | "on_track";
  category: "score" | "assessment" | "team" | "successor" | "task" | "timeline";
  headline: string;
  detail: string;
  action_url: string;
  dismissed: boolean;
}

interface InsightCounts {
  high: number;
  medium: number;
  on_track: number;
  total: number;
}

const ASSESSMENT_NAMES: Record<string, string> = {
  "owner-readiness": "mySuccession Snapshot",
  "owner-readiness-exit-timeline": "Owner Readiness and Exit Timeline",
  "business-profile": "Business Profile",
  "business-valuation": "Business Valuation",
  "business-optimization": "Business Optimization Review",
  "advisory-team": "Advisor Team Assembly",
  "financial-readiness": "Financial Readiness Assessment",
  "successor-strategy": "Successor Strategy",
  "governance-family": "Governance and Family Dynamics",
  "exit-structure": "Exit Structure and Tax Optimization",
  "mysuccession-plan": "mySuccession Plan Assembly",
};

const REQUIRED_ADVISOR_TYPES = [
  {
    type: "estate_planning_lawyer",
    headline: "No estate planning lawyer on your advisory team",
    detail: "Critical for structuring your succession and minimising tax exposure",
  },
  {
    type: "accountant_cpa",
    headline: "No accountant or CPA on your advisory team",
    detail: "Essential for financial due diligence and transition planning",
  },
  {
    type: "corporate_business_lawyer",
    headline: "No corporate lawyer on your advisory team",
    detail: "Required for share transfers, buy-sell agreements and legal structuring",
  },
];

function getAccessibleModules(completedModuleIds: string[]): string[] {
  const completed = new Set(completedModuleIds);
  const accessible: string[] = [];
  accessible.push('owner-readiness');
  if (completed.has('owner-readiness')) {
    accessible.push('owner-readiness-exit-timeline');
    accessible.push('business-profile');
  }
  if (completed.has('owner-readiness-exit-timeline') && completed.has('business-profile')) {
    accessible.push('business-valuation');
  }
  if (completed.has('business-valuation')) {
    accessible.push('business-optimization');
  }
  if (completed.has('business-optimization')) {
    accessible.push('advisory-team');
    accessible.push('exit-structure');
    accessible.push('financial-readiness');
    accessible.push('governance-family');
    accessible.push('successor-strategy');
  }
  if (
    completed.has('advisory-team') &&
    completed.has('exit-structure') &&
    completed.has('financial-readiness') &&
    completed.has('governance-family') &&
    completed.has('successor-strategy')
  ) {
    accessible.push('mysuccession-plan');
  }
  return accessible;
}

export function useSuccessionInsights() {
  const { user } = useAuth();
  const { effectiveTeamId, loading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data: rawData, isLoading } = useQuery({
    queryKey: ["successionInsights", userId, effectiveTeamId],
    queryFn: async () => {
      const [
        pillarRes,
        pillarsDefRes,
        modulesRes,
        teamRes,
        successorRes,
        tasksRes,
        teamsRes,
        dismissedRes,
      ] = await Promise.all([
        supabase
          .from("user_scores")
          .select("score_value, entity_id")
          .eq("user_id", userId!)
          .eq("team_id", effectiveTeamId!)
          .eq("entity_type", "pillar"),
        supabase.from("scoring_pillars").select("id, title"),
        supabase
          .from("assessment_modules")
          .select("module_id, status")
          .eq("user_id", userId!),
        supabase
          .from("team_members")
          .select("advisor_type")
          .eq("team_id", effectiveTeamId!)
          .neq("member_role", "owner"),
        supabase
          .from("successor_candidates")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId!),
        supabase
          .from("tasks")
          .select("id", { count: "exact", head: true })
          .eq("team_id", effectiveTeamId!)
          .neq("status", "completed")
          .lt("due_date", new Date().toISOString()),
        supabase
          .from("teams")
          .select("succession_target_date")
          .eq("id", effectiveTeamId!)
          .maybeSingle(),
        supabase
          .from("dismissed_insights")
          .select("insight_key")
          .eq("user_id", userId!),
      ]);

      return {
        pillarScores: pillarRes.data ?? [],
        pillarDefs: pillarsDefRes.data ?? [],
        modules: modulesRes.data ?? [],
        teamAdvisors: teamRes.data ?? [],
        successorCount: successorRes.count ?? 0,
        overdueCount: tasksRes.count ?? 0,
        successionDate: teamsRes.data?.succession_target_date ?? null,
        dismissedKeys: new Set(
          (dismissedRes.data ?? []).map((d) => d.insight_key)
        ),
      };
    },
    staleTime: 30_000,
    enabled: !!userId && !!effectiveTeamId && !teamLoading,
  });

  const insights = useMemo<Insight[]>(() => {
    if (!rawData) return [];
    const {
      pillarScores,
      pillarDefs,
      modules,
      teamAdvisors,
      successorCount,
      overdueCount,
      successionDate,
      dismissedKeys,
    } = rawData;

    // If zero assessments are complete, return empty — insights require at least A1
    const completedModulesList = modules.filter((m) => m.status === "completed" || m.status === "complete");
    if (completedModulesList.length === 0) return [];

    const all: Insight[] = [];
    const pillarLookup = new Map(pillarDefs.map((p) => [p.id, p.title]));

    // PILLAR SCORE INSIGHTS
    for (const row of pillarScores) {
      const score = Number(row.score_value);
      const name = pillarLookup.get(row.entity_id!) ?? "Unknown Pillar";
      const key = `pillar_score_${row.entity_id}`;
      let priority: Insight["priority"] = "on_track";
      if (score < 40) priority = "high";
      else if (score <= 60) priority = "medium";

      all.push({
        key,
        priority,
        category: "score",
        headline: priority === "on_track" ? `${name} is on track` : `${name} needs attention`,
        detail:
          priority === "on_track"
            ? `Current score: ${Math.round(score)}% — above the recommended threshold`
            : `Current score: ${Math.round(score)}% — below the recommended threshold`,
        action_url: "/plan",
        dismissed: dismissedKeys.has(key),
      });
    }

    // ASSESSMENT GAP INSIGHTS
    const completedModuleIds = modules
      .filter((m) => m.status === "completed" || m.status === "complete")
      .map((m) => m.module_id);
    const completedModules = new Set(completedModuleIds);
    const accessibleModules = new Set(getAccessibleModules(completedModuleIds));
    for (const [moduleId, name] of Object.entries(ASSESSMENT_NAMES)) {
      if (!completedModules.has(moduleId) && accessibleModules.has(moduleId)) {
        const key = `assessment_incomplete_${moduleId}`;
        all.push({
          key,
          priority: "high",
          category: "assessment",
          headline: `${name} is not yet complete`,
          detail: "Complete this assessment to improve your score and unlock insights",
          action_url: "/getting-started",
          dismissed: dismissedKeys.has(key),
        });
      }
    }

    // SUCCESSOR GAP
    if (successorCount === 0) {
      const key = "successor_gap";
      all.push({
        key,
        priority: "high",
        category: "successor",
        headline: "No successor has been identified",
        detail: "This is the most cited succession failure point for financial advisors",
        action_url: "/successors",
        dismissed: dismissedKeys.has(key),
      });
    }

    // TEAM GAP INSIGHTS
    const advisorTypes = new Set(
      teamAdvisors.map((t) => t.advisor_type).filter(Boolean)
    );
    for (const req of REQUIRED_ADVISOR_TYPES) {
      if (!advisorTypes.has(req.type)) {
        const key = `team_gap_${req.type}`;
        all.push({
          key,
          priority: "medium",
          category: "team",
          headline: req.headline,
          detail: req.detail,
          action_url: "/team",
          dismissed: dismissedKeys.has(key),
        });
      }
    }

    // TASK ALERT
    if (overdueCount > 0) {
      const key = "tasks_overdue";
      all.push({
        key,
        priority: "medium",
        category: "task",
        headline: `${overdueCount} task${overdueCount === 1 ? " is" : "s are"} overdue`,
        detail: "Momentum on key actions is a leading indicator of succession readiness",
        action_url: "/tasks",
        dismissed: dismissedKeys.has(key),
      });
    }

    // TIMELINE ALERT
    if (successionDate) {
      const target = new Date(successionDate);
      const now = new Date();
      const yearsRemaining = (target.getTime() - now.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      if (yearsRemaining > 0 && yearsRemaining <= 3) {
        const key = "timeline_proximity";
        const rounded = yearsRemaining < 1 ? "less than 1 year" : `${Math.round(yearsRemaining)} year${Math.round(yearsRemaining) === 1 ? "" : "s"}`;
        all.push({
          key,
          priority: "high",
          category: "timeline",
          headline: `Your target exit is within ${rounded}`,
          detail: "Critical milestones need to be in motion now to meet your timeline",
          action_url: "/plan",
          dismissed: dismissedKeys.has(key),
        });
      }
    }

    // Filter dismissed and sort — no cap here, cap applied at display level
    const priorityOrder = { high: 0, medium: 1, on_track: 2 };
    return all
      .filter((i) => !i.dismissed)
      .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [rawData]);

  const counts = useMemo<InsightCounts>(() => {
    const c = { high: 0, medium: 0, on_track: 0, total: 0 };
    for (const i of insights) {
      c[i.priority]++;
      c.total++;
    }
    return c;
  }, [insights]);

  const displayInsights = useMemo(() => insights.slice(0, 10), [insights]);

  const dismissMutation = useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase
        .from("dismissed_insights")
        .upsert(
          { user_id: user!.id, insight_key: key },
          { onConflict: "user_id,insight_key" }
        );
      if (error) throw error;
    },
    onMutate: async (key) => {
      await queryClient.cancelQueries({ queryKey: ["successionInsights"] });
      queryClient.setQueryData(
        ["successionInsights", userId, effectiveTeamId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            dismissedKeys: new Set([...old.dismissedKeys, key]),
          };
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["successionInsights"] });
    },
  });

  return {
    insights: displayInsights,
    allInsightsCount: insights.length,
    counts,
    isLoading,
    dismissInsight: (key: string) => dismissMutation.mutate(key),
  };
}
